import { KyInstance, type Options } from "ky";
import { Comment, CommentArray, ReactionType } from "~src/types/posts";
import { Uuid, UuidString } from "~src/utils/UUID";

export class CommentsClient {
  private http_client: KyInstance;
  constructor(http_client: KyInstance) {
    this.http_client = http_client;
  }

  async get_comments(options: GetCommentsOptions, config: Options = {}): Promise<Comment[]> {
    //interface for compatibility with the searchParams of Ky (they only support basic types)
    interface GetCommentsOptionsCompatible {
      timestamp: string;
      direction?: 'forwards' | 'backwards';
      number: number;
      group_id: string;
      post_id: string;
    }

    //restructure options to make it compatible with the searchParams
    const compatible_options: GetCommentsOptionsCompatible = {
      timestamp: options.timestamp,
      direction: options.direction,
      number: options.number,
      group_id: options.group_id.toString(),
      post_id: options.post_id.toString(),
    };

    const res = (await this.http_client
      .get('comments', { searchParams: { ...compatible_options }, ...config })
      .json()) as {
        comments: CommentArray;
      };

    const comments = CommentArray.parse(res.comments);
    return comments;
  }

  async post_comment(options: PostCommentoptions, config: Options = {}): Promise<UuidString> {
    const res = (await this.http_client
      .post('comments', {
        json: { ...options },
        ...config,
      })
      .json()) as {
        comment_id: string;
      };

    return Uuid.from_string(res.comment_id);
  }

  async delete_comment(comment_id: UuidString, config: Options = {}) {
    await this.http_client.delete('comments', {
      searchParams: { id: comment_id.toString() },
      ...config,
    });
  }

  async add_reaction(
    comment_id: UuidString,
    reaction_type: ReactionType,
    config: Options = {},
  ) {
    await this.http_client.post("reactions/comment", {
      json: { comment_id, reaction_type },
      ...config,
    });
  }

  async remove_reaction(comment_id: UuidString, config: Options = {}) {
    await this.http_client.delete("reactions/comment", {
      searchParams: { comment_id: comment_id.toString() },
      ...config,
    });
  }
}

export interface GetCommentsOptions {
  timestamp: string;
  direction?: 'forwards' | 'backwards';
  number: number;
  group_id: UuidString;
  post_id: UuidString;
}

export interface CommentMention {
  mentioned_user_id: UuidString;
  character_index: number;
}

export interface PostCommentoptions {
  text_content: string;
  mentioned_users: CommentMention[];
  post_id: UuidString;
  group_id: UuidString;
}
